/**
 * CloudGM Company Agent — Hub page (Feb 2026 / iter-181).
 * Top-level 5-button hub at `/manage/cloudgm-agent`.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, FolderOpen, Briefcase, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { AgentPageShell } from './agent/AgentPageShell';
import { usePerms } from '../permissions/PermissionsContext';

// iter-244 (Feb 2026) — Removed the standalone "Residencies/Visas" and "Other
// Documents" cards. Both flows are now reached from within a specific company
// file (`AgentCompanyDetailPage`) so the operator never has to pick the
// company again — the URL param carries the id.
// iter-253 (Feb 2026) — Added the "خدمات الشركات المسجلة" card as a primary
// entry point alongside registration + company files.
const cards = [
  {
    id: 'create-company',
    testid: 'agent-create-company-btn',
    icon: Building2,
    color: '#10b981',
    title_ar: 'تسجيل الشركات',
    title_en: 'Company Registration',
    desc_ar: 'تسجيل شركة جديدة ومتابعة الطلبات',
    desc_en: 'Register a new company and follow up on requests',
    path: '/manage/cloudgm-agent/company-registration',
  },
  {
    id: 'track-companies',
    testid: 'agent-track-companies-btn',
    icon: FolderOpen,
    color: '#a855f7',
    title_ar: 'ملف الشركات',
    title_en: 'Companies File',
    desc_ar: 'الشركات + الإقامات والتأشيرات + الوثائق الأخرى',
    desc_en: 'Companies + residencies/visas + other documents',
    path: '/manage/cloudgm-agent/companies',
  },
  {
    id: 'registered-companies-services',
    testid: 'agent-registered-services-btn',
    icon: Briefcase,
    color: '#06b6d4',
    title_ar: 'خدمات الشركات المسجلة',
    title_en: 'Registered Companies Services',
    desc_ar: 'إدراج طلبات الخدمات ومتابعة المراحل الخمس',
    desc_en: 'Insert service requests and track the 5-stage pipeline',
    path: '/registered-companies-services',
  },
  {
    id: 'commission-audit',
    testid: 'agent-commission-audit-btn',
    icon: ShieldCheck,
    color: '#d4af37',
    title_ar: 'التدقيق على العمولة المحصلة',
    title_en: 'Commission Collection Audit',
    desc_ar: 'التحقق من العمولات المحصلة ورفع سند إيداعها',
    desc_en: 'Verify collected commissions and upload deposit receipts',
    path: '/manage/cloudgm-agent/commission-audit',
  },
];

const CloudGMAgentHub = () => {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC (للإشراف)', label_en: 'CLOUDGM FZ LLC (Supervision)', path: '/dashboard?platform=fzlc' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: null },
  ];

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/dashboard?platform=fzlc')}>
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-3 text-white">
          Cloud GM Company Agent
        </h1>
        <p className="text-[#808080]">
          {language === 'ar' ? 'وكالة كلاود جي إم — إدارة الشركات والإقامات والتأشيرات' : 'CloudGM Agency — companies, residencies and visas'}
        </p>
      </div>

      {/* 5-button grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {visiblePages(cards, 'path').map((c, i) => {
          const disabled = c.path === null;
          const Icon = c.icon;
          return (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              whileHover={disabled ? {} : { y: -6, scale: 1.02 }}
              whileTap={disabled ? {} : { scale: 0.98 }}
              onClick={() => (disabled ? null : navigate(c.path))}
              disabled={disabled}
              data-testid={c.testid}
              className={`group relative bg-white/[0.02] backdrop-blur-xl border rounded-2xl p-8 text-center overflow-hidden transition-all
                ${disabled ? 'opacity-60 cursor-not-allowed border-white/5' : 'border-white/10 hover:bg-white/[0.04] cursor-pointer'}`}
            >
              {!disabled && (
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ boxShadow: `inset 0 0 0 1px ${c.color}60, 0 8px 32px ${c.color}20` }}
                />
              )}
              <div className="relative">
                <div
                  className="w-20 h-20 mx-auto mb-4 rounded-xl flex items-center justify-center border transition-transform duration-300"
                  style={{ backgroundColor: `${c.color}15`, borderColor: `${c.color}30` }}
                >
                  <Icon className="h-10 w-10" style={{ color: c.color }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">
                  {language === 'ar' ? c.title_ar : c.title_en}
                </h3>
                <p className="text-xs text-[#808080] px-2">
                  {language === 'ar' ? c.desc_ar : c.desc_en}
                </p>
                {disabled && (
                  <span className="inline-block mt-3 px-2 py-0.5 text-[10px] rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                    {language === 'ar' ? 'قريباً' : 'Coming soon'}
                  </span>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
    </AgentPageShell>
  );
};

export default CloudGMAgentHub;
